/**
 * Overflow Detection Utilities
 * 
 * These utilities help detect when translated text is too large for UI elements
 * across different languages in the UI preview.
 */

export interface OverflowIssue {
  element: HTMLElement;
  elementSelector: string;
  locaKey: string;
  language: string;
  languageName: string;
  translatedText: string;
  overflowType: 'width' | 'height' | 'both';
  actualSize: { width: number; height: number };
  availableSize: { width: number; height: number };
  overflowAmount: { width: number; height: number };
}

export interface OverflowReport {
  issues: OverflowIssue[];
  totalElements: number;
  elementsWithIssues: number;
  languagesWithIssues: Set<string>;
}

/**
 * Check if an element has overflow issues
 */
export function detectElementOverflow(element: HTMLElement): {
  hasOverflow: boolean;
  overflowType: 'width' | 'height' | 'both' | null;
  actualSize: { width: number; height: number };
  availableSize: { width: number; height: number };
  overflowAmount: { width: number; height: number };
} {
  // Get the computed style to check overflow settings
  const computedStyle = window.getComputedStyle(element);
  const originalOverflow = computedStyle.overflow;
  const originalOverflowX = computedStyle.overflowX;
  const originalOverflowY = computedStyle.overflowY;
  
  // Temporarily set overflow to visible to get accurate measurements
  // This is necessary because overflow:hidden can prevent scrollWidth/scrollHeight 
  // from reflecting the true content size
  const needsTemporaryOverflow = 
    originalOverflow === 'hidden' || originalOverflow === 'clip' ||
    originalOverflowX === 'hidden' || originalOverflowX === 'clip' ||
    originalOverflowY === 'hidden' || originalOverflowY === 'clip';
  
  if (needsTemporaryOverflow) {
    element.style.overflow = 'visible';
  }
  
  // Get the element's dimensions
  const clientWidth = element.clientWidth;
  const clientHeight = element.clientHeight;
  const scrollWidth = element.scrollWidth;
  const scrollHeight = element.scrollHeight;
  
  // Restore original overflow
  if (needsTemporaryOverflow) {
    element.style.overflow = originalOverflow;
  }
  
  // Allow for small rounding differences (1px tolerance)
  const tolerance = 1;
  const widthOverflow = scrollWidth > (clientWidth + tolerance);
  const heightOverflow = scrollHeight > (clientHeight + tolerance);
  
  let overflowType: 'width' | 'height' | 'both' | null = null;
  if (widthOverflow && heightOverflow) {
    overflowType = 'both';
  } else if (widthOverflow) {
    overflowType = 'width';
  } else if (heightOverflow) {
    overflowType = 'height';
  }
  
  return {
    hasOverflow: widthOverflow || heightOverflow,
    overflowType,
    actualSize: {
      width: scrollWidth,
      height: scrollHeight
    },
    availableSize: {
      width: clientWidth,
      height: clientHeight
    },
    overflowAmount: {
      width: Math.max(0, scrollWidth - clientWidth),
      height: Math.max(0, scrollHeight - clientHeight)
    }
  };
}

/**
 * Generate a CSS selector for an element
 */
function generateElementSelector(element: HTMLElement): string {
  const path: string[] = [];
  let current: HTMLElement | null = element;
  
  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();
    
    // Add ID if available
    if (current.id) {
      selector += `#${current.id}`;
      path.unshift(selector);
      break; // ID is unique, no need to go further
    }
    
    // Add classes if available
    if (current.className && typeof current.className === 'string') {
      const classes = current.className.trim().split(/\s+/).filter(c => c);
      if (classes.length > 0) {
        selector += '.' + classes.join('.');
      }
    }
    
    // Add nth-child if there are siblings of the same type
    const parent = current.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter(
        child => child.tagName === current!.tagName
      );
      if (siblings.length > 1) {
        const index = siblings.indexOf(current) + 1;
        selector += `:nth-of-type(${index})`;
      }
    }
    
    path.unshift(selector);
    current = current.parentElement;
  }
  
  return path.join(' > ');
}

/**
 * Find all elements in the document that contain locakeys
 * Returns the immediate parent element that contains the text
 */
export function findElementsWithLocaKeys(doc: Document): Map<string, HTMLElement[]> {
  const locaKeyPattern = /\{\{?([a-zA-Z0-9_.-]+)\}\}?/g;
  const elementsMap = new Map<string, HTMLElement[]>();
  
  // Get the window from the document
  const docWindow = doc.defaultView || window;
  
  // Get all text nodes
  const walker = doc.createTreeWalker(
    doc.body,
    NodeFilter.SHOW_TEXT,
    null
  );
  
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const text = node.textContent || '';
    const matches = Array.from(text.matchAll(locaKeyPattern));
    
    if (matches.length > 0 && node.parentElement) {
      matches.forEach(match => {
        const locaKey = match[1];
        
        // Find the closest element that should be measured
        // Walk up the DOM tree to find the first element with explicit size constraints
        let targetElement = node.parentElement!;
        
        // Walk up to find a constrained container
        try {
          let currentElement = node.parentElement;
          const maxDepth = 8; // Increased depth to find containers further up
          let depth = 0;
          let bestCandidate = targetElement; // Keep track of the best candidate
          
          while (currentElement && depth < maxDepth) {
            const computedStyle = docWindow.getComputedStyle(currentElement);
            
            // Parse numeric values from CSS
            const widthValue = parseFloat(computedStyle.width);
            const heightValue = parseFloat(computedStyle.height);
            
            // Check for explicit pixel-based constraints (not percentages)
            const hasExplicitWidth = computedStyle.width !== 'auto' && 
                                    !isNaN(widthValue) &&
                                    widthValue > 0 &&
                                    !computedStyle.width.includes('%');
            const hasExplicitHeight = computedStyle.height !== 'auto' && 
                                     !isNaN(heightValue) &&
                                     heightValue > 0 &&
                                     !computedStyle.height.includes('%');
            const hasMaxConstraints = (computedStyle.maxWidth && computedStyle.maxWidth !== 'none') ||
                                     (computedStyle.maxHeight && computedStyle.maxHeight !== 'none');
            
            // If this element has explicit size constraints, it's a good candidate
            if (hasExplicitWidth || hasExplicitHeight || hasMaxConstraints) {
              bestCandidate = currentElement;
              // Keep looking for parent containers with constraints
              // This ensures we find .board-text instead of stopping at <p>
            }
            
            // Stop if we hit the body or a major container
            if (currentElement.tagName === 'BODY' || 
                currentElement.classList?.contains('board-container')) {
              break;
            }
            
            // Move up to parent
            currentElement = currentElement.parentElement;
            depth++;
          }
          
          targetElement = bestCandidate;
        } catch (e) {
          // If getComputedStyle fails, just use the parent element as-is
        }
        
        const existingElements = elementsMap.get(locaKey) || [];
        
        // Only add if not already in the list
        if (!existingElements.includes(targetElement)) {
          existingElements.push(targetElement);
          elementsMap.set(locaKey, existingElements);
        }
      });
    }
  }
  
  return elementsMap;
}

/**
 * Check all languages for overflow issues in a given iframe document
 */
export async function checkAllLanguagesForOverflow(
  iframeDoc: Document,
  html: string,
  css: string,
  languages: Array<{ code: string; name: string }>,
  stringsItems: any[],
  replaceLocaKeysFn: (html: string, languageCode: string) => string
): Promise<OverflowReport> {
  const issues: OverflowIssue[] = [];
  const languagesWithIssues = new Set<string>();
  const elementsChecked = new Set<string>();
  
  // Find all elements that originally had locakeys in the source HTML
  const locaKeyPattern = /\{\{?([a-zA-Z0-9_.-]+)\}\}?/g;
  const locaKeysInHTML = Array.from(html.matchAll(locaKeyPattern));
  const uniqueLocaKeys = Array.from(new Set(locaKeysInHTML.map(match => match[1])));
  
  if (uniqueLocaKeys.length === 0) {
    return {
      issues: [],
      totalElements: 0,
      elementsWithIssues: 0,
      languagesWithIssues: new Set()
    };
  }
  
  // For each language, render the content and check for overflow
  for (const language of languages) {
    // Skip 'source' as it's just the original text
    if (language.code === 'source') continue;
    
    // Replace locakeys with translations
    const processedHtml = replaceLocaKeysFn(html, language.code);
    
    // Create a temporary iframe to render this language version
    const tempIframe = document.createElement('iframe');
    tempIframe.style.position = 'absolute';
    tempIframe.style.left = '-9999px';
    tempIframe.style.width = iframeDoc.defaultView?.innerWidth + 'px' || '1024px';
    tempIframe.style.height = iframeDoc.defaultView?.innerHeight + 'px' || '768px';
    document.body.appendChild(tempIframe);
    
    try {
      const tempDoc = tempIframe.contentDocument;
      if (!tempDoc) continue;
      
      // Write the content
      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              body {
                margin: 0;
                padding: 20px;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
              }
              ${css}
            </style>
          </head>
          <body>
            ${processedHtml}
          </body>
        </html>
      `;
      
      tempDoc.open();
      tempDoc.write(content);
      tempDoc.close();
      
      // Wait for rendering to complete
      // Using write() method means content loads synchronously, but we need to wait for layout
      await new Promise<void>(resolve => {
        // Wait for the document to be parsed and initial layout to complete
        const checkReady = () => {
          if (tempDoc.body && tempDoc.body.children.length > 0) {
            // Content is there, wait a bit more for styles to apply
            setTimeout(() => resolve(), 100);
          } else {
            // Content not ready yet, check again soon
            setTimeout(checkReady, 50);
          }
        };
        
        // Start checking immediately
        setTimeout(checkReady, 50);
      });
      
      // Find which LocaKeys were used in which elements
      const elementsToCheck = new Map<HTMLElement, string[]>(); // element -> locakeys
      const tempWindow = tempDoc.defaultView;
      
      if (!tempWindow) {
        continue;
      }
      
      // Parse the processed HTML to map elements to their locakeys
      for (const locaKey of uniqueLocaKeys) {
        // Find the string item for this locakey
        const stringItem = stringsItems.find(item => item.abbrev === locaKey);
        if (!stringItem) {
          continue;
        }
        
        // Get the translation
        const translation = stringItem.translations?.find(
          (trans: any) => trans.language_code === language.code
        );
        
        // Determine what text should be in the rendered HTML
        // Match the logic from replaceLocaKeys in UIPage.tsx
        let textToFind = '';
        if (translation && translation.translated_text) {
          textToFind = translation.translated_text;
        } else {
          // Fallback to source text (same as replaceLocaKeys does)
          textToFind = stringItem.source_text || '';
        }
        
        if (!textToFind || !textToFind.trim()) {
          continue;
        }
        
        // Strip HTML tags from the text since we're comparing against textContent
        // which doesn't include HTML tags (they become actual elements)
        const textToFindStripped = textToFind.replace(/<[^>]*>/g, '').trim();
        
        // Find all text nodes containing this translation
        const walker = tempDoc.createTreeWalker(
          tempDoc.body,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        let node: Node | null;
        let foundCount = 0;
        while ((node = walker.nextNode())) {
          const text = node.textContent || '';
          const trimmedText = text.trim();
          
          // Check if this text node contains the translation
          // Use includes for more flexible matching (handles cases with extra whitespace)
          if (trimmedText && textToFindStripped && 
              (trimmedText === textToFindStripped || trimmedText.includes(textToFindStripped)) && 
              node.parentElement) {
            
            foundCount++;
            
            // Get the actual element that should be measured
            // Walk up the DOM tree to find the first element with explicit size constraints
            let targetElement = node.parentElement;
            
            // Walk up to find a constrained container
            try {
              let currentElement = node.parentElement;
              const maxDepth = 8; // Increased depth to find containers further up
              let depth = 0;
              let bestCandidate = targetElement; // Keep track of the best candidate
              
              while (currentElement && depth < maxDepth) {
                const computedStyle = tempWindow.getComputedStyle(currentElement);
                
                // Parse numeric values from CSS
                const widthValue = parseFloat(computedStyle.width);
                const heightValue = parseFloat(computedStyle.height);
                
                // Check for explicit pixel-based constraints (not percentages)
                const hasExplicitWidth = computedStyle.width !== 'auto' && 
                                        !isNaN(widthValue) &&
                                        widthValue > 0 &&
                                        !computedStyle.width.includes('%');
                const hasExplicitHeight = computedStyle.height !== 'auto' && 
                                         !isNaN(heightValue) &&
                                         heightValue > 0 &&
                                         !computedStyle.height.includes('%');
                const hasMaxConstraints = (computedStyle.maxWidth && computedStyle.maxWidth !== 'none') ||
                                         (computedStyle.maxHeight && computedStyle.maxHeight !== 'none');
                
                // If this element has explicit size constraints, it's a good candidate
                if (hasExplicitWidth || hasExplicitHeight || hasMaxConstraints) {
                  bestCandidate = currentElement;
                  // Keep looking for parent containers with constraints
                  // This ensures we find .board-text instead of stopping at <p>
                }
                
                // Stop if we hit the body or a major container
                if (currentElement.tagName === 'BODY' || 
                    currentElement.classList?.contains('board-container')) {
                  break;
                }
                
                // Move up to parent
                currentElement = currentElement.parentElement;
                depth++;
              }
              
              targetElement = bestCandidate;
            } catch (e) {
              // If getComputedStyle fails, just use the element as-is
            }
            
            const keys = elementsToCheck.get(targetElement) || [];
            if (!keys.includes(locaKey)) {
              keys.push(locaKey);
              elementsToCheck.set(targetElement, keys);
              
              // Debug: log which element was selected for this locakey
              const computedStyle = tempWindow.getComputedStyle(targetElement);
              const textPreview = translation?.translated_text?.substring(0, 50) || stringItem?.source_text?.substring(0, 50) || '';
              console.log(`[Overflow Detection] ${language.name} - Found element for ${locaKey}:`, {
                tagName: targetElement.tagName,
                className: targetElement.className,
                width: computedStyle.width,
                height: computedStyle.height,
                clientWidth: targetElement.clientWidth,
                clientHeight: targetElement.clientHeight,
                scrollWidth: targetElement.scrollWidth,
                scrollHeight: targetElement.scrollHeight,
                textPreview: textPreview + (textPreview.length >= 50 ? '...' : '')
              });
            }
          }
        }
      }
      
      // Check each element for overflow
      for (const [element, locaKeys] of elementsToCheck.entries()) {
        const overflowCheck = detectElementOverflow(element);
        const selector = generateElementSelector(element);
        
        // Debug logging
        console.log(`[Overflow Detection] ${language.name}:`, {
          selector,
          locaKeys,
          hasOverflow: overflowCheck.hasOverflow,
          overflowType: overflowCheck.overflowType,
          actualSize: overflowCheck.actualSize,
          availableSize: overflowCheck.availableSize,
          overflowAmount: overflowCheck.overflowAmount
        });
        
        if (overflowCheck.hasOverflow) {
          locaKeys.forEach(locaKey => {
            const stringItem = stringsItems.find(item => item.abbrev === locaKey);
            if (stringItem) {
              const translation = stringItem.translations?.find(
                (trans: any) => trans.language_code === language.code
              );
              
              issues.push({
                element: element,
                elementSelector: selector,
                locaKey: locaKey,
                language: language.code,
                languageName: language.name,
                translatedText: translation?.translated_text || stringItem.source_text,
                overflowType: overflowCheck.overflowType!,
                actualSize: overflowCheck.actualSize,
                availableSize: overflowCheck.availableSize,
                overflowAmount: overflowCheck.overflowAmount
              });
              
              languagesWithIssues.add(language.code);
              elementsChecked.add(selector);
            }
          });
        }
      }
    } catch (error) {
      // Continue with other languages even if one fails
    } finally {
      // Clean up - ensure iframe is removed even if there was an error
      try {
        if (document.body.contains(tempIframe)) {
          document.body.removeChild(tempIframe);
        }
      } catch (e) {
        // Silently handle cleanup errors
      }
    }
  }
  
  return {
    issues,
    totalElements: elementsChecked.size,
    elementsWithIssues: new Set(issues.map(i => i.elementSelector)).size,
    languagesWithIssues
  };
}

/**
 * Highlight elements with overflow issues in the preview
 */
export function highlightOverflowElements(
  iframeDoc: Document,
  issues: OverflowIssue[],
  currentLanguage: string
): void {
  // Remove existing highlights
  const existingHighlights = iframeDoc.querySelectorAll('[data-overflow-highlight]');
  existingHighlights.forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.outline = '';
      el.style.position = '';
      el.removeAttribute('data-overflow-highlight');
      
      // Remove any existing badges
      const badge = el.querySelector('.overflow-badge');
      if (badge) {
        badge.remove();
      }
    }
  });
  
  // Group issues by element selector
  const issuesByElement = new Map<string, OverflowIssue[]>();
  issues.forEach(issue => {
    const existing = issuesByElement.get(issue.elementSelector) || [];
    existing.push(issue);
    issuesByElement.set(issue.elementSelector, existing);
  });
  
  // Highlight elements
  issuesByElement.forEach((elementIssues, selector) => {
    try {
      const elements = iframeDoc.querySelectorAll(selector);
      elements.forEach(element => {
        if (!(element instanceof HTMLElement)) return;
        
        // Check if current language has issues
        const hasCurrentLanguageIssue = elementIssues.some(i => i.language === currentLanguage);
        
        // Determine highlight color based on severity
        const affectedLanguages = new Set(elementIssues.map(i => i.language));
        const severity = affectedLanguages.size;
        
        let outlineColor = '#fbbf24'; // amber for warnings
        if (severity > 3) {
          outlineColor = '#ef4444'; // red for critical
        } else if (hasCurrentLanguageIssue) {
          outlineColor = '#f97316'; // orange for current language
        }
        
        // Apply highlight
        element.style.outline = `2px solid ${outlineColor}`;
        element.style.outlineOffset = '2px';
        element.setAttribute('data-overflow-highlight', 'true');
        
        // Add a badge showing number of affected languages
        const badge = iframeDoc.createElement('div');
        badge.className = 'overflow-badge';
        badge.textContent = `${affectedLanguages.size} ${affectedLanguages.size === 1 ? 'language' : 'languages'}`;
        badge.style.cssText = `
          position: absolute;
          top: -10px;
          right: -10px;
          background: ${outlineColor};
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
          z-index: 1000;
          pointer-events: none;
          white-space: nowrap;
        `;
        
        // Make parent positioned if not already
        const position = window.getComputedStyle(element).position;
        if (position === 'static') {
          element.style.position = 'relative';
        }
        
        element.appendChild(badge);
      });
    } catch (error) {
      // Silently handle highlight errors
    }
  });
}

